import React, { useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { Icon, Typography } from '@packages/ui/shared';
import { PublicationForm } from '@web/vin-sessions/types';
import { StyleSheet } from 'react-native';
import { UsePublicationFormReturnType } from './hooks/use-publication-form';
import { vinSessionService } from '@web/services/singletons';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { useTranslation } from 'react-i18next';

const logger = getLogger('vin-sessions');

interface Props {
    publicationInfo: PublicationForm;
    actions?: UsePublicationFormReturnType['actions'];
}

const useStyles = createUseStyles({
    clearIcon: {
        position: 'absolute',
        right: 8,
        top: 8,
        zIndex: 2,
        backgroundColor: colors.white,
        cursor: 'pointer',
    },
    code: {
        backgroundColor: colors.blueFour,
        borderRadius: '4px',
        padding: '2px 8px',
        display: 'flex',
        alignItems: 'center',
        color: colors.blueOne,
    },
    closeButton: {
        cursor: 'pointer',
        background: 'transparent',
        border: 'none',
        paddingRight: 0,
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
        position: 'relative',
    },
    inputWrapper: {
        border: '1px solid #999da3',
        borderRadius: '4px',
        padding: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
    },
    inputBox: {
        flex: '1',
        border: 'none',
        outline: 'none',
        minWidth: '100px',
    },
    dropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        border: '1px solid #ced4da',
        borderRadius: '4px',
        background: '#fff',
        zIndex: 10,
    },
    dropdownItem: {
        cursor: 'pointer',
        padding: '12px 16px',
        '&:hover': {
            background: '#e9ecef',
        },
    },
    dropdownItemSelected: {
        background: '#d4e7f7',
    },
    hidden: {
        display: 'none',
    },
});

export const DtccTypeAhead = ({ publicationInfo, actions }: Props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState<string>();
    const [dtccOptions, setDtccOptions] = useState<string[]>([]);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const { abortSignalRef, signalAbort } = useAbortController();

    const styles = StyleSheet.create({
        code: {
            fontWeight: '700',
            lineHeight: 15.95,
            fontSize: 11,
        },
    });

    const clearSelection = async () => {
        signalAbort();
    };

    const handleRemove = (index: number) => {
        const newDtccs = publicationInfo.dtcCodes.filter((_, i) => i !== index);
        actions?.handleAddDtcCodes(newDtccs);
        actions?.handleTagsChange(true);
    };

    const handleInputChange = async (query: string) => {
        try {
            setSelectedIndex(-1);
            if (query.length == 0 || !query.replace(/\s/g, '').length) {
                setDtccOptions([]);
                setIsDropdownVisible(false);
                await clearSelection();
                return;
            }

            const response = await vinSessionService.getDTCC({
                query: query,
                signal: abortSignalRef.current,
                ignoreCache: true,
            });

            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                throw response.data;
            }

            if (Array.isArray(response.data?.dtcCodes) && response.data.dtcCodes.length > 0) {
                const formattedOptions = response.data.dtcCodes.map(
                    (item: any) => `${item.dtcCode}(${item.description})`
                );
                setDtccOptions(formattedOptions);
                setIsDropdownVisible(true);
            } else {
                setDtccOptions(['No matches found']);
                setIsDropdownVisible(true);
            }
        } catch (e) {
            alert('Error: ' + e + '. Please refresh & try again.');
            logger.error(`Failed to fetch the DTC codes for the query [${query}]`, { error: e, query });
        }
    };

    const handleClearSelection = async () => {
        actions?.handleAddDtcCodes([]);
        actions?.handleTagsChange(true);
        setIsDropdownVisible(false);
        setInputValue('');
        await clearSelection();
    };

    const handleSelectionChange = (selected: any) => {
        logger.trace(`Selected the DTC code [${selected}]`, { dtcCode: selected });
        const isUserSelection = selected.length > 0;
        if (isUserSelection && !publicationInfo.dtcCodes.includes(selected)) {
            const newDtcCodes = [...publicationInfo.dtcCodes, selected];
            actions?.handleAddDtcCodes(newDtcCodes);
            actions?.handleTagsChange(true);
            setDtccOptions([]);
            setIsDropdownVisible(false);
        }
    };

    return (
        <div className={classes.container}>
            <div className={classes.inputWrapper}>
                {publicationInfo.dtcCodes.map((dtcCode, index) => (
                    <div key={index} className={classes.code}>
                        <Typography color="blueOne" style={styles.code}>
                            {dtcCode}
                        </Typography>
                        <button
                            className={classes.closeButton}
                            onClick={() => handleRemove(index)}
                            aria-label="Remove code"
                        >
                            <Icon name="x-close" color="blueOne" size={16} />
                        </button>
                    </div>
                ))}
                <input
                    type="text"
                    multiple
                    value={inputValue}
                    className={classes.inputBox}
                    placeholder={
                        publicationInfo.dtcCodes.length === 0
                            ? t('stisPublications:documentInfo.placeholder.dtcc', 'DTC Codes')
                            : ''
                    }
                    style={{ borderColor: colors.grayFive }}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        handleInputChange(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (dtccOptions.length > 0 && dtccOptions[0] !== 'No matches found') {
                                const indexToSelect = selectedIndex >= 0 ? selectedIndex : 0;
                                handleSelectionChange(dtccOptions[indexToSelect]);
                                setInputValue('');
                                setSelectedIndex(-1);
                            }
                        } else if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            if (dtccOptions.length > 0 && dtccOptions[0] !== 'No matches found') {
                                setSelectedIndex((prev) => (prev < dtccOptions.length - 1 ? prev + 1 : prev));
                            }
                        } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            if (dtccOptions.length > 0) {
                                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
                            }
                        } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setIsDropdownVisible(false);
                            setSelectedIndex(-1);
                        }
                    }}
                    onBlur={() => setIsDropdownVisible(false)}
                />
                <div className={classes.clearIcon}>
                    {publicationInfo.dtcCodes.length > 0 && <Icon name="x-close" onPress={handleClearSelection} />}
                </div>
            </div>
            {dtccOptions.length > 0 && (
                <div
                    className={`${classes.dropdown} ${isDropdownVisible ? '' : classes.hidden}`}
                    onMouseDown={(e) => {
                        e.preventDefault();
                    }}
                >
                    {dtccOptions.map((dtccOption, index) => (
                        <div
                            key={`dtcc-option-${index}`}
                            className={`${classes.dropdownItem} ${
                                index === selectedIndex ? classes.dropdownItemSelected : ''
                            }`}
                            onClick={() => {
                                handleSelectionChange(dtccOption);
                                setInputValue('');
                                setSelectedIndex(-1);
                            }}
                        >
                            <span>{dtccOption}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
